import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { CollarInstrument, GPSInstrument, MethodType } from '@types';
import { CacheService } from 'app/core/services/cache.service';
import { ActualType, BudgetType, CompanyType, DrillRigType, RockCode, Site_ConfigType, SiteType, StructureDescriptionType, SubSiteType } from 'app/modules/admin/forms/forms.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, switchMap, take, tap } from 'rxjs';
import { ElementType } from './manage/element/element.types';
import { LaboratoryTemplateType } from './manage/laboratory-templates/laboratory-templates.types';

@Injectable({
	providedIn: 'root'
})
export class SitesService {

	apiUrl = environment.url;

	// Private
	private _sites: BehaviorSubject<SiteType[] | null> = new BehaviorSubject(null);
	private _site: BehaviorSubject<SiteType | null> = new BehaviorSubject(null);
	private _subsites: BehaviorSubject<any | null> = new BehaviorSubject(null);
	private _company: BehaviorSubject<CompanyType | null> = new BehaviorSubject(null);
	private _companies: BehaviorSubject<CompanyType[] | null> = new BehaviorSubject(null);
	private _drillrig: BehaviorSubject<DrillRigType | null> = new BehaviorSubject(null);
	private _drillrigs: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
	private _instruments: BehaviorSubject<CollarInstrument[] | null> = new BehaviorSubject(null);
	private _rockCodebysiteid: BehaviorSubject<RockCode[] | null> = new BehaviorSubject(null);
	private _drillrigsbysiteid: BehaviorSubject<DrillRigType[] | null> = new BehaviorSubject(null);
	private _structuresbysiteid: BehaviorSubject<StructureDescriptionType[] | null> = new BehaviorSubject(null);
	private budgetsbysite: BehaviorSubject<BudgetType[] | null> = new BehaviorSubject(null);
	private actualsbysite: BehaviorSubject<ActualType[] | null> = new BehaviorSubject(null);
	private _siteconfigsbysiteid: BehaviorSubject<Site_ConfigType | null> = new BehaviorSubject(null);
	private _formdata: BehaviorSubject<any | null> = new BehaviorSubject(null);
	private _laboratoryTemplatesBySiteId: BehaviorSubject<LaboratoryTemplateType[] | null> = new BehaviorSubject(null);

	/**
	 * Constructor
	 */
	constructor(
		private _httpClient: HttpClient,
		private location: Location,
		private cacheService: CacheService
	) {
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter de Sites
	 *
	 * Um acessor de um conjuto de todos os Sites
	 *
	 * @readonly
	 * @type {Observable<SiteType[]>}
	 * @memberof SitesService
	 */
	get sites$(): Observable<SiteType[]> {
		return this._sites.asObservable();
	}

	/**
	 * Getter de Site
	 *
	 * Um acessor de site individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get site$(): Observable<SiteType> {
		return this._site.asObservable();
	}

	/**
	 * Getter de Site
	 *
	 * Um acessor de site individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get drillrig$(): Observable<DrillRigType> {
		return this._drillrig.asObservable();
	}

	/**
	 * Getter de Site
	 *
	 * Um acessor de site individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get instrumentsbysiteid$(): Observable<CollarInstrument[]> {
		return this._instruments.asObservable();
	}


	/**
	 * Getter de Site
	 *
	 * Um acessor de site individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get drillrigs$(): Observable<any[]> {
		return this._drillrigs.asObservable();
	}

	/**
	 * Getter de Rockcode by site
	 *
	 * Um acessor de Rockcode individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get rockCodebysiteid$(): Observable<RockCode[]> {
		return this._rockCodebysiteid.asObservable();
	}


	/**
	 * Getter de Drillrig by site
	 *
	 * Um acessor de Drillrig individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get drillrigsbysiteid$(): Observable<DrillRigType[]> {
		return this._drillrigsbysiteid.asObservable();
	}
	/**
	 * Getter de structures by site id
	 *
	 * Um acessor de structures by site id
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get structuresbysiteid$(): Observable<StructureDescriptionType[]> {
		return this._structuresbysiteid.asObservable();
	}
	/**
	 * Getter de budgets by site id
	 *
	 * Um acessor de budgets by site id
	 *
	 * @readonly
	 * @type {Observable<BudgetType>}
	 * @memberof SitesService
	 */
	get budgetsbysite$(): Observable<BudgetType[]> {
		return this.budgetsbysite.asObservable();
	}
	/**
	 * Getter de actual by site id
	 *
	 * Um acessor de actual by site id
	 *
	 * @readonly
	 * @type {Observable<ActualType>}
	 * @memberof SitesService
	 */
	get actualsbysite$(): Observable<ActualType[]> {
		return this.actualsbysite.asObservable();
	}
	/**
	 * Getter de Compnaies
	 *
	 * Um acessor de muitos companies
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get companies$(): Observable<CompanyType[]> {
		return this._companies.asObservable();
	}
	/**
	 * Getter de Site
	 *
	 * Um acessor de site individual
	 *
	 * @readonly
	 * @type {Observable<SiteType>}
	 * @memberof SitesService
	 */
	get company$(): Observable<CompanyType> {
		return this._company.asObservable();
	}

	/**openfas
	 * Getter de Subsites
	 *
	 * Um acessor de um conjuto de subsites dentro de um Site
	 *
	 * @readonly
	 * @type {Observable<any>}
	 * @memberof SitesService
	 */
	get subsites$(): Observable<any> {
		return this._subsites.asObservable();
	}

	/**
	 * Getter de Configurações do Site_ConfigType
	 *
	 * Um acessor de um conjuto de configurações dentro de um Site_ConfigType
	 *
	 * @readonly
	 * @type {Observable<any>}
	 * @memberof SitesService
	 */
	get siteconfigsbysiteid$(): Observable<Site_ConfigType> {
		return this._siteconfigsbysiteid.asObservable();
	}

	/**openfas
	 * Getter de Configurações do Site
	 *
	 * Um acessor de um conjuto de configurações dentro de um Site
	 *
	 * @readonly
	 * @type {Observable<any>}
	 * @memberof SitesService
	 */
	get formdata$(): Observable<any> {
		return this._formdata.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Novos metodos
	// -----------------------------------------------------------------------------------------------------

	/**
	 * ### Get Sites
	 *
	 * Busca todos os sites cadastrados no banco
	 *
	 * @return um array contendo todas as entidades de site do banco
	 * @memberof SitesService
	 */
	getSites(): Observable<SiteType[]> {
		return this._httpClient.get<SiteType[]>(this.apiUrl + 'site/all').pipe(
			tap((response: any) => {
				this._sites.next(response);
			})
		);
	}


	getSitesByQueryBodyPost(body: any): Observable<SiteType[]> {
		return this._httpClient.post<SiteType[]>(this.apiUrl + 'site/all', body).pipe(
			tap((response: any) => {
				this._sites.next(response);
			})
		);
	}



	/**
	 * ### Get Sites
	 *
	 * Busca todos os sites cadastrados no banco
	 *
	 * @return um array contendo todas as entidades de site do banco
	 * @memberof SitesService
	 */
	getSubSites(): Observable<SubSiteType[]> {
		return this._httpClient.get<SubSiteType[]>(this.apiUrl + 'subsite/all').pipe(
			tap((response: any) => {
				this._subsites.next(response);
			})
		);
	}

	/**
	 * ### Get Site By ID
	 *
	 * Busca um site a partir de uma ID informada
	 *
	 * @param id a ID que o banco deve buscar
	 * @return uma entidade de Site, quando encontrada no banco
	 * @memberof SitesService
	 */
	getSiteById(id: string): Observable<SiteType> {
		return this._httpClient.get<SiteType>(this.apiUrl + 'site/' + id).pipe(
			tap((response: any) => {
				this._site.next(response);
			})
		);
	}

	/**
	 * ### Update Site
	 */
	updateSite(id: string, body: any): Observable<SiteType> {
		return this._httpClient.patch<SiteType>(this.apiUrl + 'site/' + id, body).pipe(
			tap((response: SiteType) => {
				this._site.next(response);
			})
		);
	}


	/**
	 * ### Get Site By ID
	 *
	 * Busca um site a partir de uma ID informada
	 *
	 * @param id a ID que o banco deve buscar
	 * @return uma entidade de Site, quando encontrada no banco
	 * @memberof SitesService
	 */
	getSubsitesBySiteID(id: string): Observable<any> {
		return this._httpClient.get<any>(this.apiUrl + 'site/' + id + '/subSites').pipe(
			tap((response: any) => {
				this._subsites.next(response);
			})
		);
	}

	// New method to get just the subsites list for move operations
	getSubsitesList(id: string): Observable<any[]> {
		return this._httpClient.get<any[]>(this.apiUrl + 'site/' + id + '/subsites-list');
	}

	/**
	 * ### Get Subsites By Sites ID In
	 *
	 * Busca todos os subsites a partir de uma lista de IDs de sites
	 *
	 * @param ids uma lista de IDs de sites
	 * @return um array contendo todas as entidades de SubSiteType correspondentes aos IDs fornecidos
	 * @memberof SitesService
	 */
	getSubsitesBySitesIdIn(ids: string[]): Observable<SubSiteType[]> {
		return this._httpClient.post<SubSiteType[]>(this.apiUrl + 'subsite/sites', { id: ids });
	}

	/**
	 * ### Get Campaigns By SubSite ID
	 *
	 * Busca todas as campanhas a partir de uma lista de IDs de subsites
	 *
	 * @param ids uma lista de IDs de subsites
	 * @return um array contendo todas as entidades de Campaign correspondentes aos IDs fornecidos
	 * @memberof SitesService
	 */
	getCampaignsBySubSiteIdIn(ids: string[]): Observable<any[]> {
		return this._httpClient.post<any[]>(this.apiUrl + 'campaign/subsites', { subSiteId: ids });
	}

	/**
	 * ### Get Collars By Campaign ID
	 *
	 * Busca todos os colares a partir de uma lista de IDs de campanhas
	 *
	 * @param ids uma lista de IDs de campanhas
	 * @return um array contendo todas as entidades de Collar correspondentes aos IDs fornecidos
	 * @memberof SitesService
	 */
	getCollarsByCampaignIdIn(ids: string[]): Observable<any[]> {
		return this._httpClient.post<any[]>(this.apiUrl + 'collar/campaigns', { campaignId: ids });
	}

	/**
	 * ### Get Despatches By Collar ID
	 *
	 * Busca todos os despachos a partir de uma lista de IDs de colares
	 *
	 * @param ids uma lista de IDs de colares
	 * @return um array contendo todas as entidades de Despatch correspondentes aos IDs fornecidos
	 * @memberof SitesService
	 */
	getDespatchesByCollarIdIn(ids: string[]): Observable<any[]> {
		return this._httpClient.post<any[]>(this.apiUrl + 'despatch/collars', { ids: ids });
	}

	/**
	 * ### Get Laboratory Templates By Site ID
	 *
	 * Busca todos os templates de laboratório com o mesmo site id
	 *
	 * @param id a ID do site a ser buscado
	 * @return entidades de LaboratoryTemplate, quando encontrada no banco
	 * @memberof SitesService
	 */
	getLaboratoryTemplatesBySiteID(id: string): Observable<LaboratoryTemplateType[]> {
		return this._httpClient.get<LaboratoryTemplateType[]>(`${this.apiUrl}laboratory-template/site/${id}`).pipe(
			tap((response: LaboratoryTemplateType[]) => {
				this._laboratoryTemplatesBySiteId.next(response);
			})
		);
	}


	/**
	 * ### Get Elements By Site ID
	 *
	 * Busca todos os elementos com o mesmo site id
	 *
	 * @param siteId a ID do site a ser buscado
	 * @return entidades de elementos, quando encontradas no banco
	 * @memberof SitesService
	 */
	getElementsBySiteId(siteId: string): Observable<ElementType[]> {
		return this._httpClient.get<ElementType[]>(`${this.apiUrl}elements/site/${siteId}`);
	}


	/**
 * ### Get Elements By Site ID
 *
 * Busca todos os elementos com o mesmo site id
 *
 * @param siteId a ID do site a ser buscado
 * @return entidades de elementos, quando encontradas no banco
 * @memberof SitesService
 */
	getMethodsBySiteId(siteId: string): Observable<MethodType[]> {
		return this._httpClient.get<MethodType[]>(`${this.apiUrl}methods/site/${siteId}`);
	}

	/**
	 * ### Get All Methods
	 *
	 * Busca todos os métodos cadastrados no banco
	 *
	 * @return um array contendo todas as entidades de método do banco
	 * @memberof SitesService
	 */
	getAllMethods(): Observable<MethodType[]> {
		return this._httpClient.get<MethodType[]>(`${this.apiUrl}methods`);
	}

	/**
 * ### Update Method
 *
 * Updates a method using PATCH request
 *
 * @param methodId the ID of the method to update
 * @param updateData the data to update
 * @return updated method
 * @memberof SitesService
 */
	updateMethod(methodId: string, updateData: any): Observable<MethodType> {
		return this._httpClient.patch<MethodType>(`${this.apiUrl}methods/${methodId}`, updateData);
	}

	/**
	 * ### Remove Element From Method
	 *
	 * Removes an element from a method
	 *
	 * @param methodId the ID of the method
	 * @param methodElementId the ID of the method element to remove
	 * @return updated method
	 * @memberof SitesService
	 */
	removeElementFromMethod(methodId: string, methodElementId: string): Observable<MethodType> {
		return this._httpClient.patch<MethodType>(`${this.apiUrl}methods/${methodId}/elements/remove`, {
			methodElementId: methodElementId
		});
	}

	/**
	 * ### Add Element To Method
	 *
	 * Adds a single element to a method
	 *
	 * @param elementData the element data to add
	 * @return updated method
	 * @memberof SitesService
	 */
	addElementToMethod(elementData: any): Observable<MethodType> {
		return this._httpClient.post<MethodType>(`${this.apiUrl}methods/${elementData.methodId}/elements`, elementData);
	}

	/**
	 * ### Update Method Element
	 *
	 * Updates a method element
	 *
	 * @param methodElementId the ID of the method element to update
	 * @param updateData the data to update
	 * @return updated method
	 * @memberof SitesService
	 */
	updateMethodElement(methodElementId: string, updateData: any): Observable<MethodType> {
		return this._httpClient.patch<MethodType>(`${this.apiUrl}method-elements/${methodElementId}`, updateData);
	}

	/**
	 * Update SiteConfig
	 *
	 * @param id
	 * @param card
	 */
	updateSiteConfig(id: string, body: any): Observable<any> {
		return this._siteconfigsbysiteid.pipe(
			take(1),
			switchMap(siteconfig => this._httpClient.patch<any>(this.apiUrl + `site-config/${id}`,
				body
			).pipe(
				map((updatedCard) => {

					// Find the card and update it
					siteconfig = updatedCard

					// Update the board
					this._siteconfigsbysiteid.next(siteconfig);


					// Return the updated card
					return updatedCard;
				})
			))
		);
	}

	/**
	 * Getter de Laboratory Templates by Site ID
	 *
	 * Um acessor de laboratory templates by site id
	 *
	 * @readonly
	 * @type {Observable<LaboratoryTemplate[]>}
	 * @memberof SitesService
	 */
	get laboratoryTemplatesBySiteId$(): Observable<LaboratoryTemplateType[]> {
		return this._laboratoryTemplatesBySiteId.asObservable();
	}

	/**
	 * ### Get All Companies
	 *
	 * Busca todos os companies cadastrados no banco
	 *
	 * @return um array contendo todas as entidades de Company do banco
	 * @memberof SitesService
	 */
	getAllCompanies(): Observable<CompanyType[]> {
		return this._httpClient.get<CompanyType[]>(this.apiUrl + 'company').pipe(
			tap((response: CompanyType[]) => {
				this._companies.next(response);
			})
		);
	}

	/**
	 * ### Get Company By ID
	 *
	 * Busca um company a partir de uma ID informada
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return uma entidade de Company, quando encontrada no banco
	 * @memberof SitesService
	 */
	getCompanyByID(id: string): Observable<CompanyType> {
		return this._httpClient.get<CompanyType>(this.apiUrl + 'company/' + id).pipe(
			tap((response: CompanyType) => {
				this._company.next(response);
			})
		);
	}

	/**
	 * ### Get Company By name query
	 *
	 * Busca um company a partir de uma ID informada
	 *
	 * @param name o nome  a ser buscada
	 * @return uma entidade de Company, quando encontrada no banco
	 * @memberof SitesService
	 */
	getCompanyBySiteNameContains(siteid: string): Observable<CompanyType> {
		return this._httpClient.get<CompanyType>(this.apiUrl + 'company?sitename=' + siteid).pipe(
			tap((response: CompanyType) => {
				this._company.next(response);
			})
		);
	}

	/**
	 * ### Get All Drill Rigs
	 *
	 * Busca todos os Drill Rigs cadastrados no banco
	 *
	 * @return um array contendo todas as entidades de Drill Rigs do banco
	 * @memberof SitesService
	 */
	getAllDrillRigs(): Observable<any[]> {
		return this._httpClient.get<any[]>(this.apiUrl + 'drill-rig/all').pipe(
			tap((response: any[]) => {
				this._drillrigs.next(response);
			})
		);
	}

	/**
	 * ### Get Company By ID
	 *
	 * Busca um company a partir de uma ID informada
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return uma entidade de Company, quando encontrada no banco
	 * @memberof SitesService
	 */
	getDrillRigByID(id: string): Observable<DrillRigType> {
		return this._httpClient.get<DrillRigType>(this.apiUrl + 'drill-rig/' + id).pipe(
			tap((response: DrillRigType) => {
				this._drillrig.next(response);
			})
		);
	}

	/**
	 * ### Get All Drillrigs By Site ID
	 *
	 * Busca todos os drillrigs com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return uma entidade de drillrig, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllDrillRigBySiteID(id: string): Observable<DrillRigType[]> {
		return this._httpClient.get<DrillRigType[]>(this.apiUrl + 'drill-rig/site/' + id).pipe(
			tap((response: DrillRigType[]) => {
				this._drillrigsbysiteid.next(response);
			})
		);
	}

	/**
	 * ### Get All RockCodes By Site ID
	 *
	 * Busca todos os RockCodes com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return uma entidade de RockCodes, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllRockCodesBySiteID(id: string): Observable<RockCode[]> {
		return this._httpClient.get<RockCode[]>(this.apiUrl + 'rock-code/' + id).pipe(
			tap((response: RockCode[]) => {
				this._rockCodebysiteid.next(response);
			})
		);
	}

	/**
	 * ### Get All Instrument By Site ID
	 *
	 * Busca todos os Instrument com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllInstrumentsBySiteID(id: string): Observable<CollarInstrument[]> {
		return this._httpClient.get<CollarInstrument[]>(this.apiUrl + 'collar-instrument/site/' + id).pipe(
			tap((response: CollarInstrument[]) => {
				this._instruments.next(response);
			})
		);
	}

	/**
	 * ### Get GPS Instrument By Site ID
	 *
	 * Busca todos os Instrument com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllGPSInstrumentsBySiteID(id: string): Observable<{data: GPSInstrument[]}> {
		return this._httpClient.get<{data: GPSInstrument[]}>(this.apiUrl + 'gps-instrument', { params: { siteId: id } });
	}

	/**
	 * ### Get All Instrument By Site ID
	 *
	 * Busca todos os Instrument com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllStructureDescriptionsBySiteID(id: string): Observable<StructureDescriptionType[]> {
		return this._httpClient.get<StructureDescriptionType[]>(this.apiUrl + 'structure-description/site/' + id).pipe(
			tap((response: StructureDescriptionType[]) => {
				this._structuresbysiteid.next(response);
			})
		);
	}
	/**
	 * ### Get All Budgets by Site ID
	 *
	 * Busca todos os registros de budget com o mesmo site id
	 *
	 * @param id a ID do site a ser buscado
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllBudgetsBySiteID(id: string): Observable<BudgetType[]> {
		return this._httpClient.get<BudgetType[]>(`${this.apiUrl}budget/site/${id}/all`).pipe(
			tap((response: BudgetType[]) => {
				this.budgetsbysite.next(response);
			})
		);
	}
	/**
	 * ### Get All Actuals by Site ID
	 *
	 * Busca todos os registros de actuals com o mesmo site id
	 *
	 * @param id a ID do site a ser buscado
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getAllActualsBySiteID(id: string): Observable<ActualType[]> {
		return this._httpClient.get<ActualType[]>(`${this.apiUrl}actual/site/${id}/all`).pipe(
			tap((response: ActualType[]) => {
				this.actualsbysite.next(response);
			})
		);
	}

	/**
	 * ### Get All Instrument By Site ID
	 *
	 * Busca todos os Instrument com o mesmo site id
	 *
	 * @param id a ID da entidade a ser buscada
	 * @return entidades de Instrument, quando encontrada no banco
	 * @memberof SitesService
	 */
	getConfigsBySiteID(id: string): Observable<Site_ConfigType> {
		return this._httpClient.get<Site_ConfigType>(`${this.apiUrl}site-config/site/${id}`).pipe(
			tap((response: Site_ConfigType) => {
				this._siteconfigsbysiteid.next(response);
			})
		);
	}

	getCollarsBySubsiteID(id: string): Observable<any[]> {
		return this._httpClient.get<any[]>(this.apiUrl + 'subsite/' + id + '/collars');
	}

	getFormsDataBySiteID(id: string): Observable<any> {
		return this._httpClient.get<any>(this.apiUrl + 'field/site/' + id).pipe(
			tap(async (response: any) => {

				const formFields = {};
				const formNames = [];

				for (const [key, value] of Object.entries(response)) {
					formFields[key.toLocaleLowerCase()] = value;
					formNames.push(key.toLocaleLowerCase());
				};

				await this.cacheService.setFormFields(formFields);
				await this.cacheService.setFormNames(formNames);

				this._formdata.next({ formFields, formNames });
			})
		);
	};

	goBack(): void {
		this.location.back();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	// Method to get all collars in a site (across all subsites)
	getCollarsBySiteId(siteId: string): Observable<any[]> {
		return this._httpClient.get<any[]>(this.apiUrl + 'site/' + siteId + '/collars');
	}

	// Method to move holes to a different target (subsite)
	moveHolesToTarget(data: { holeIds: string[], destinationTargetId: string }): Observable<any> {
		return this._httpClient.post<any>(this.apiUrl + 'collar/move-to-target', data);
	}
}
